import { useCallback } from 'react';
import { useFileUploadStore } from '../store/fileUploadStore';
import {
  requestUploadUrl,
  uploadToS3,
  confirmUpload,
  isValidMimeType,
  isValidFileSize,
  getMaxFileSize,
  formatFileSize,
} from '../api/files';
import type { AttachedFile } from '@/types/files';

interface UseFileUploadOptions {
  conversationId: string | null;
  onUploadComplete?: (file: AttachedFile) => void;
  onUploadError?: (file: AttachedFile, error: Error) => void;
}

export function useFileUpload(options: UseFileUploadOptions) {
  const { conversationId, onUploadComplete, onUploadError } = options;

  const {
    files,
    addFile,
    removeFile,
    updateStatus,
    updateProgress,
    setFileId,
    clearAllFiles,
  } = useFileUploadStore();

  // Convert Map to array for easier iteration
  const filesArray = Array.from(files.values());
  const hasFiles = filesArray.length > 0;
  const allUploaded = filesArray.every((f) => f.status === 'uploaded');
  const isUploading = filesArray.some((f) => f.status === 'uploading');
  const uploadedFileIds = filesArray
    .filter((f) => f.status === 'uploaded' && f.fileId)
    .map((f) => f.fileId!);

  /**
   * Upload a single file
   * Files can be uploaded without a conversation - they will be associated when the message is sent
   */
  const uploadFile = useCallback(
    async (file: File): Promise<void> => {
      // Validate file type
      if (!isValidMimeType(file.type)) {
        throw new Error(`File type "${file.type}" is not supported`);
      }

      // Validate file size
      if (!isValidFileSize(file)) {
        const maxSize = formatFileSize(getMaxFileSize(file.type));
        throw new Error(`File exceeds maximum size of ${maxSize}`);
      }

      // Add file to store
      const tempId = addFile(file);

      try {
        // Update status to uploading
        updateStatus(tempId, 'uploading');

        // Step 1: Request presigned URL (conversationId is optional)
        const { fileId, uploadUrl, downloadUrl } = await requestUploadUrl({
          filename: file.name,
          mimeType: file.type,
          conversationId: conversationId || undefined,
          sizeBytes: file.size,
        });

        updateProgress(tempId, 10);

        // Step 2: Upload to S3
        await uploadToS3(uploadUrl, file, (progress) => {
          // Map 0-100 to 10-90 range
          const adjustedProgress = 10 + progress * 0.8;
          updateProgress(tempId, Math.round(adjustedProgress));
        });

        updateProgress(tempId, 95);

        // Step 3: Confirm upload
        await confirmUpload(fileId);

        // Update store with final state
        setFileId(tempId, fileId, downloadUrl);

        // Notify completion
        const updatedFile = useFileUploadStore.getState().getFile(tempId);
        if (updatedFile && onUploadComplete) {
          onUploadComplete(updatedFile);
        }
      } catch (error) {
        const err = error instanceof Error ? error : new Error('Upload failed');
        updateStatus(tempId, 'failed', err.message);

        // Notify error
        const failedFile = useFileUploadStore.getState().getFile(tempId);
        if (failedFile && onUploadError) {
          onUploadError(failedFile, err);
        }

        throw err;
      }
    },
    [
      conversationId,
      addFile,
      updateStatus,
      updateProgress,
      setFileId,
      onUploadComplete,
      onUploadError,
    ]
  );

  /**
   * Handle file input change
   * Files can be uploaded without a conversation - they will be associated when the message is sent
   */
  const handleFilesSelected = useCallback(
    async (fileList: FileList | null) => {
      if (!fileList || fileList.length === 0) return;

      const fileArray = Array.from(fileList);

      // Upload files in parallel
      const uploadPromises = fileArray.map((file) =>
        uploadFile(file).catch((err) => {
          console.error(`Failed to upload ${file.name}:`, err);
        })
      );

      await Promise.allSettled(uploadPromises);
    },
    [uploadFile]
  );

  /**
   * Retry failed upload
   */
  const retryUpload = useCallback(
    async (tempId: string) => {
      const file = useFileUploadStore.getState().getFile(tempId);
      if (!file || file.status !== 'failed') return;

      // Remove failed entry and re-upload
      removeFile(tempId);
      await uploadFile(file.file);
    },
    [removeFile, uploadFile]
  );

  /**
   * File uploads are always allowed
   * Files without a conversation will be associated when the message is sent
   */
  const canUpload = true;

  return {
    // State
    files: filesArray,
    hasFiles,
    allUploaded,
    isUploading,
    uploadedFileIds,
    canUpload,

    // Actions
    uploadFile,
    handleFilesSelected,
    removeFile,
    retryUpload,
    clearAllFiles,
  };
}
